import {
  actionLogSchema,
  SCREENSHOT_MEDIA_TYPES,
  type ActionLog,
  type AttachmentAccesses,
  type TicketAttachments,
} from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch, apiRequest } from './client';
import { ticketsQueryKey } from './tickets';

/**
 * Allegati dei consumer nel dettaglio (story #8, DEC-010). I **metadati** (stato, dimensione,
 * rifiuti) si leggono al caricamento: non sono un accesso al contenuto. Il **contenuto** si scarica
 * solo su richiesta esplicita, con una mutation e mai con una query: niente cache, prefetch o
 * nuovi tentativi automatici, perché ogni download è un accesso registrato a nome di chi lo fa.
 */

/** Chiave dello stato degli allegati di un ticket, sotto la radice `['tickets']`: chi scrive la riallinea. */
export const ticketAttachmentsQueryKey = (code: string) => [...ticketsQueryKey, 'attachments', code] as const;

/** Chiave della traccia degli accessi a un allegato (solo amministratori). */
export const attachmentAccessesQueryKey = (code: string, attachmentId: string) =>
  [...ticketAttachmentsQueryKey(code), 'accesses', attachmentId] as const;

/** Mentre la finestra di caricamento è aperta lo stato si rilegge ogni 30 secondi. */
const PENDING_REFRESH_MS = 30_000;

const attachmentsPath = (code: string) => `/tickets/${encodeURIComponent(code)}/allegati`;

/** `GET /tickets/:code/allegati`: per schermata e log lo stato (assenza dichiarata), i metadati e i rifiuti. */
export function useTicketAttachments(code: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ticketAttachmentsQueryKey(code),
    queryFn: () => apiRequest<TicketAttachments>(attachmentsPath(code)),
    enabled,
    refetchInterval: (query) =>
      Object.values(query.state.data?.allegati ?? {}).some((slot) => slot.stato === 'in_attesa')
        ? PENDING_REFRESH_MS
        : false,
  });
}

/** `GET /tickets/:code/allegati/:id/accessi` (amministratori): chi ha aperto il contenuto e quando. */
export function useAttachmentAccesses(code: string, attachmentId: string) {
  return useQuery({
    queryKey: attachmentAccessesQueryKey(code, attachmentId),
    queryFn: () =>
      apiRequest<AttachmentAccesses>(`${attachmentsPath(code)}/${encodeURIComponent(attachmentId)}/accessi`),
  });
}

/**
 * `GET /tickets/:code/allegati/:id/contenuto`: scarica il file. L'API registra l'accesso prima di
 * consegnarlo; un errore arriva come `ApiError` con il codice dell'API (410 eliminato per retention,
 * 503 archivio non disponibile).
 */
export async function downloadAttachment(code: string, attachmentId: string): Promise<Blob> {
  const response = await apiFetch(`${attachmentsPath(code)}/${encodeURIComponent(attachmentId)}/contenuto`);
  return response.blob();
}

/** Contenuto che non è ciò che il visore si aspetta: non si mostra. */
export class UnreadableAttachmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnreadableAttachmentError';
  }
}

/**
 * Scarica la schermata e ne verifica il tipo: si mostra solo un'immagine PNG, JPEG o WebP, come
 * quelle che l'API accetta (DEC-010), mai un contenuto di altro tipo come documento.
 */
export async function downloadScreenshot(code: string, attachmentId: string): Promise<Blob> {
  const blob = await downloadAttachment(code, attachmentId);
  if (!(SCREENSHOT_MEDIA_TYPES as readonly string[]).includes(blob.type)) {
    throw new UnreadableAttachmentError(
      'Il contenuto ricevuto non è un’immagine PNG, JPEG o WebP: non viene mostrato.'
    );
  }
  return blob;
}

/** Scarica il log e lo valida con lo schema del contratto: un log fuori schema non si mostra. */
export async function downloadActionLog(code: string, attachmentId: string): Promise<ActionLog> {
  const blob = await downloadAttachment(code, attachmentId);
  let parsed: unknown;
  try {
    parsed = JSON.parse(await blob.text());
  } catch {
    throw new UnreadableAttachmentError('Il log ricevuto non è un documento JSON leggibile.');
  }
  const result = actionLogSchema.safeParse(parsed);
  if (!result.success) {
    throw new UnreadableAttachmentError('Il log ricevuto non rispetta lo schema del contratto: non viene mostrato.');
  }
  return result.data;
}

/**
 * Apertura su richiesta di un allegato. Una mutation: parte solo da un'azione dell'utente e non
 * ritenta da sola (un secondo tentativo sarebbe un secondo accesso registrato). `gcTime: 0` e il
 * `reset()` alla chiusura del visore fanno sì che il contenuto non resti nella cache delle mutation.
 * Dopo l'apertura si riallineano gli accessi e, se il contenuto è stato eliminato nel frattempo
 * (410), lo stato degli allegati. Un 401 passa dalla cache delle mutation e porta all'accesso.
 */
export function useOpenAttachment<T>(
  code: string,
  attachmentId: string,
  download: (code: string, attachmentId: string) => Promise<T>
) {
  const queryClient = useQueryClient();
  return useMutation<T, Error, void>({
    mutationFn: () => download(code, attachmentId),
    retry: false,
    gcTime: 0,
    onSettled: async (_data, error) => {
      if (error instanceof ApiError && error.status === 410) {
        await queryClient.invalidateQueries({ queryKey: ticketAttachmentsQueryKey(code) });
      }
      await queryClient.invalidateQueries({ queryKey: attachmentAccessesQueryKey(code, attachmentId) });
    },
  });
}
